import { ref, Ref } from 'vue';
import { throttleRaf } from '@/utils/throttleRaf';
import { AnnotationGeometry, Annotation } from '../types/annotation.types';
import _ from 'lodash';

export function useAnnotationGeometry(
  annotation: Ref<Annotation>,
  annotationEl: Ref<any>,
  publicView: Ref<boolean>,
  showingCounterpointSidePanel: Ref<boolean>
) {
  const height = ref<number>(0);
  const lastGeometry = ref<AnnotationGeometry | null>(null);
  const geometries = ref<Record<string, any>>({});
  const excerptMaxChars = ref<number>(50);
  const charWidth = ref<number>(20);

  const annotationBoxEllipsisSize = () => {
    const boxSize = document.getElementsByClassName('annotationBoxEllipsis');
    const annotationBoxSize = Array.from(boxSize);

    const breakpoints = [
      { width: 1500, charWidth: [10, 11, 15] },
      { width: 1600, charWidth: [10, 15, 20] },
      { width: 1700, charWidth: [10, 18, 22] },
      { width: 1800, charWidth: [11, 19, 25] },
      { width: 1908, charWidth: [15, 20, 26] },
      { width: 2000, charWidth: [16, 20, 28] },
      { width: 2100, charWidth: [17, 20, 30] },
      { width: 2150, charWidth: [19, 22, 34] },
      { width: 2240, charWidth: [20, 25, 35] },
      { width: 2310, charWidth: [21, 30, 38] },
      { width: 2340, charWidth: [22, 30, 39] },
      { width: 2700, charWidth: [25, 33, 42] },
      { width: Infinity, charWidth: [28, 45, 55] },
    ];

    annotationBoxSize.forEach((element: Element) => {
      const htmlElement = element as HTMLElement;
      const boxWidth = htmlElement.offsetWidth;

      const breakpoint = breakpoints.find(bp => window.innerWidth <= bp.width);
      if (!breakpoint) return;

      // This would need access to counterpointData from store
      // For now, using a simple calculation
      charWidth.value = breakpoint.charWidth[1];

      const averageCharacterWidth = htmlElement.clientWidth / charWidth.value;
      const maxCharsFirstThreeLines = Math.floor((3 * boxWidth) / averageCharacterWidth);
      excerptMaxChars.value = maxCharsFirstThreeLines;
    });
  };

  const setAnnotationGeometry = (): AnnotationGeometry | null => {
    if (!annotationEl.value?.$el) return null;

    const outlineContainer = document.getElementById('outlineContainer');
    const outlineWidth = outlineContainer?.offsetWidth || 0;

    const geometry: AnnotationGeometry = {
      annotationId: annotation.value._id,
      top: annotationEl.value.$el.offsetTop - (publicView.value ? 50 : 0),
      left: showingCounterpointSidePanel.value
        ? annotationEl.value.$el.offsetLeft - outlineWidth
        : annotationEl.value.$el.offsetLeft,
      width: annotationEl.value.$el.offsetWidth,
      height: annotationEl.value.$el.offsetHeight,
    };

    if (!_.isEqual(geometry, lastGeometry.value)) {
      lastGeometry.value = geometry;
      return geometry;
    }

    return null;
  };

  const setupAnnotationGeometry = () => {
    const geometry = setAnnotationGeometry();
    if (annotationEl.value?.$el) {
      height.value = annotationEl.value.$el.scrollHeight;
    }
    return geometry;
  };

  const setupAnnotationGeometryThrottled = throttleRaf(setupAnnotationGeometry);

  const updateParagraphGeometries = (newGeometries: Record<string, any>) => {
    for (const key in newGeometries) {
      geometries.value[key] = newGeometries[key];
    }
    return geometries.value;
  };

  const doInputKeyDown = (): boolean => {
    if (annotationEl.value?.$el && height.value !== annotationEl.value.$el.scrollHeight) {
      height.value = annotationEl.value.$el.scrollHeight;
      return true; // Signal resize needed
    }
    return false;
  };

  return {
    height,
    lastGeometry,
    geometries,
    excerptMaxChars,
    charWidth,
    annotationBoxEllipsisSize,
    setAnnotationGeometry,
    setupAnnotationGeometry,
    setupAnnotationGeometryThrottled,
    updateParagraphGeometries,
    doInputKeyDown,
  };
}
